import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Download, CheckCircle2, FileText } from 'lucide-react';

// Accepts: 0612345678, 06 12 34 56 78, 06.12.34.56.78, 06-12-34-56-78
const FR_PHONE_RE = /^0[1-9](?:[ .\-]?\d{2}){4}$/;

/**
 * ProgrammeDownloadDialog — lead capture before downloading the course
 * programme PDF. Validates French phone format + RGPD consent.
 *
 * Adds an explicit "Formation d'intérêt" dropdown (defaulted to the current course)
 * that lets the prospect change their selected formation; the chosen course's
 * PDF is downloaded and the lead is tagged with that course.
 */
export const ProgrammeDownloadDialog = ({ open, onOpenChange, course }) => {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', consent_rgpd: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { downloadUrl, fileName }
  const [errors, setErrors] = useState({});
  const [coursesWithPdf, setCoursesWithPdf] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(course?.id || '');

  // Load every onsite course that has a programme PDF — these are the choices
  // the prospect can pick as "Formation d'intérêt".
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const all = await api.getCourses();
        const filtered = (all || []).filter((c) => c.programme_pdf_path);
        if (!cancelled) setCoursesWithPdf(filtered);
      } catch (_) {
        if (!cancelled) setCoursesWithPdf(course ? [course] : []);
      }
    };
    if (open) load();
    return () => { cancelled = true; };
  }, [open, course]);

  // Sync selected course with the page's current course whenever it changes
  useEffect(() => {
    if (course?.id) setSelectedCourseId(course.id);
  }, [course]);

  const selectedCourse = useMemo(
    () => coursesWithPdf.find((c) => c.id === selectedCourseId) || course,
    [coursesWithPdf, selectedCourseId, course],
  );

  const update = (k, v) => {
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Prénom requis';
    if (!form.last_name.trim()) e.last_name = 'Nom requis';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email invalide';
    if (!FR_PHONE_RE.test(form.phone.trim())) e.phone = 'Format attendu : 0X XX XX XX XX';
    if (!selectedCourseId) e.course = 'Choisissez une formation';
    if (!form.consent_rgpd) e.consent_rgpd = 'Consentement obligatoire';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const reset = () => {
    setForm({ first_name: '', last_name: '', email: '', phone: '', consent_rgpd: false });
    setDone(null); setErrors({}); setSubmitting(false);
    setSelectedCourseId(course?.id || '');
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.requestProgramme(selectedCourseId, form);
      const url = api.getProgrammeDownloadUrl(selectedCourseId, res.download_token);
      // Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = res.programme_pdf_name || 'programme.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setDone({ url, fileName: res.programme_pdf_name });
      toast.success('Téléchargement lancé — merci !');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const phoneErr = detail.find((d) => d.loc?.includes('phone'));
        if (phoneErr) {
          setErrors({ phone: phoneErr.msg });
        } else {
          toast.error('Erreur de validation');
        }
      } else {
        toast.error(detail || 'Erreur lors de la demande');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}
    >
      <DialogContent className="bg-white max-w-md" data-testid="programme-download-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#C9B896]" />
                Recevoir le programme
              </DialogTitle>
              <DialogDescription>
                Renseignez vos coordonnées pour télécharger le programme détaillé.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Prénom *</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => update('first_name', e.target.value)}
                    data-testid="lead-firstname"
                    placeholder="Marie"
                  />
                  {errors.first_name && <p className="text-xs text-red-600">{errors.first_name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Nom *</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => update('last_name', e.target.value)}
                    data-testid="lead-lastname"
                    placeholder="Dupont"
                  />
                  {errors.last_name && <p className="text-xs text-red-600">{errors.last_name}</p>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  data-testid="lead-email"
                  placeholder="marie.dupont@exemple.fr"
                />
                {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Téléphone *</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  data-testid="lead-phone"
                  placeholder="06 12 34 56 78"
                />
                {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
                <p className="text-[11px] text-muted-foreground">Format : 0X XX XX XX XX</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Formation d'intérêt *</Label>
                <select
                  value={selectedCourseId}
                  onChange={(e) => {
                    setSelectedCourseId(e.target.value);
                    if (errors.course) setErrors({ ...errors, course: null });
                  }}
                  data-testid="lead-course-of-interest"
                  className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm text-[#1F2A4A] focus:outline-none focus:ring-2 focus:ring-[#C9B896]/60"
                >
                  {coursesWithPdf.length === 0 && course && (
                    <option value={course.id}>{course.title}</option>
                  )}
                  {coursesWithPdf.map((c) => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
                {errors.course && <p className="text-xs text-red-600">{errors.course}</p>}
                {selectedCourse && selectedCourse.id !== course?.id && (
                  <p className="text-[11px] text-[#C9B896]">
                    Vous recevrez le programme de « {selectedCourse.title} ».
                  </p>
                )}
              </div>
              <label className="flex items-start gap-3 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={form.consent_rgpd}
                  onChange={(e) => update('consent_rgpd', e.target.checked)}
                  className="w-4 h-4 accent-[#C9B896] mt-0.5"
                  data-testid="lead-rgpd"
                />
                <span className="text-xs text-[#1F2A4A]/85 leading-relaxed">
                  J'accepte que mes coordonnées soient utilisées par <strong>Sellerie Académie</strong> pour
                  me transmettre le programme et me recontacter au sujet de cette formation. Conformément au RGPD,
                  je peux exercer mes droits (accès, rectification, suppression) à tout moment.
                </span>
              </label>
              {errors.consent_rgpd && <p className="text-xs text-red-600">{errors.consent_rgpd}</p>}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button" variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="flex-1 rounded-full"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full"
                  data-testid="lead-submit"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {submitting ? 'Envoi…' : 'Télécharger'}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="programme-download-success">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="font-heading text-2xl text-[#1F2A4A] mb-2">Merci !</h3>
            <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
              Votre téléchargement est en cours. Si rien ne se passe, vous pouvez réessayer ci-dessous.
            </p>
            <div className="flex gap-3 justify-center">
              <a
                href={done.url}
                download={done.fileName}
                className="inline-flex items-center gap-2 bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] px-5 py-2.5 rounded-full text-sm font-medium"
              >
                <Download className="w-4 h-4" /> Re-télécharger
              </a>
              <Button onClick={() => onOpenChange(false)} variant="outline" className="rounded-full">
                Fermer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
